import { Plus, Trash2 } from 'lucide-react';
import { Input } from 'src/components/ui/input';
import { StringListEditor } from 'src/components/shared';
import { cn } from 'src/lib/utils';
import type { VerticalProfileImagery, VerticalProfileImagerySubject } from '../types/vertical-profile.types';
import { IMAGERY_REQUIRED_OPTIONS } from '../types/vertical-profile.types';
import { EMPTY_IMAGERY_SUBJECT } from '../helpers/vertical-profile.helpers';
import { SchedulerDsButton } from './scheduler-ds-button';

const labelClassName = 'text-sm font-medium text-gray-700';

const selectClassName = cn(
  'h-10 w-full appearance-none rounded border border-gray-400 bg-white pl-4 pr-10 text-base text-black',
  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-black',
);

const textareaClassName = cn(
  'flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm',
  'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:text-sm',
);

interface ImageryGuidanceEditorProps {
  value: VerticalProfileImagery;
  onChange: (value: VerticalProfileImagery) => void;
}

export function ImageryGuidanceEditor({ value, onChange }: ImageryGuidanceEditorProps) {
  const updateSubject = (index: number, next: VerticalProfileImagerySubject) => {
    onChange({
      ...value,
      subjects: value.subjects.map((subject, itemIndex) => (itemIndex === index ? next : subject)),
    });
  };

  const addSubject = () => {
    onChange({
      ...value,
      subjects: [...value.subjects, { ...EMPTY_IMAGERY_SUBJECT }],
    });
  };

  const removeSubject = (index: number) => {
    onChange({
      ...value,
      subjects: value.subjects.filter((_, itemIndex) => itemIndex !== index),
    });
  };

  return (
    <section className="space-y-4 rounded-md border border-gray-300 bg-gray-50 p-4">
      <h3 className="text-sm font-medium uppercase tracking-wide text-gray-500">Imagery guidance</h3>

      <div className="grid gap-4 md:grid-cols-2">
        <label className="space-y-1">
          <span className={labelClassName}>Requirement</span>
          <select
            value={value.required}
            onChange={(event) => onChange({ ...value, required: event.target.value as VerticalProfileImagery['required'] })}
            className={selectClassName}
          >
            {IMAGERY_REQUIRED_OPTIONS.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </label>

        <label className="space-y-1">
          <span className={labelClassName}>Precision (0–100)</span>
          <Input
            type="number"
            min={0}
            max={100}
            value={value.precision}
            onChange={(event) => onChange({ ...value, precision: Number(event.target.value) })}
          />
        </label>

        <label className="space-y-1 md:col-span-2">
          <span className={labelClassName}>Precision label</span>
          <Input
            value={value.precision_label}
            onChange={(event) => onChange({ ...value, precision_label: event.target.value })}
          />
        </label>
      </div>

      <label className="block space-y-1">
        <span className={labelClassName}>Fidelity notes</span>
        <textarea
          value={value.fidelity_notes}
          onChange={(event) => onChange({ ...value, fidelity_notes: event.target.value })}
          className={textareaClassName}
        />
      </label>

      <label className="block space-y-1">
        <span className={labelClassName}>Shooting guidance</span>
        <textarea
          value={value.shooting_guidance}
          onChange={(event) => onChange({ ...value, shooting_guidance: event.target.value })}
          className={textareaClassName}
        />
      </label>

      <label className="block space-y-1">
        <span className={labelClassName}>AI image notes</span>
        <textarea
          value={value.ai_image_notes}
          onChange={(event) => onChange({ ...value, ai_image_notes: event.target.value })}
          className={textareaClassName}
        />
      </label>

      <StringListEditor
        label="Avoid images"
        values={value.avoid_images}
        onChange={(avoid_images) => onChange({ ...value, avoid_images })}
        placeholder="e.g. stock handshakes"
      />

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <span className={labelClassName}>Subjects</span>
          <SchedulerDsButton type="button" variant="secondary" size="sm" onClick={addSubject}>
            <Plus className="size-4" />
            Add subject
          </SchedulerDsButton>
        </div>
        {value.subjects.map((subject, index) => (
          <div key={`subject-${index}`} className="space-y-3 rounded-md border border-gray-300 bg-white p-3">
            <label className="block space-y-1">
              <span className={labelClassName}>Title</span>
              <Input
                value={subject.title}
                onChange={(event) => updateSubject(index, { ...subject, title: event.target.value })}
              />
            </label>
            <label className="block space-y-1">
              <span className={labelClassName}>Description</span>
              <textarea
                value={subject.description}
                onChange={(event) => updateSubject(index, { ...subject, description: event.target.value })}
                className={textareaClassName}
              />
            </label>
            <StringListEditor
              label="Tags"
              values={subject.tags}
              onChange={(tags) => updateSubject(index, { ...subject, tags })}
              placeholder="tag"
            />
            <SchedulerDsButton type="button" variant="danger-ghost" size="sm" onClick={() => removeSubject(index)}>
              <Trash2 className="size-4" />
              Remove subject
            </SchedulerDsButton>
          </div>
        ))}
      </div>
    </section>
  );
}
